import type { ToolType } from '../types/dashboard';

export interface PermissionConfig {
  id: string;
  displayName: string;
  description: string;
}

// Badge types
export type PermanentBadgeType = 'admin' | 'beta';
export type TemporaryBadgeType = 'new' | 'updated';
export type BadgeType = PermanentBadgeType | TemporaryBadgeType;

export interface TemporaryBadgeConfig {
  type: TemporaryBadgeType;
  // Uses tool's addedAt for expiry calculation
}

/**
 * Sections in the Add Tool catalog, in the order they render.
 *
 * `admin` is not assigned per-tool — it is derived from `adminOnly`, so an
 * admin tool cannot end up in a user-facing section by forgetting a field.
 */
export const TOOL_CATEGORIES = ['gamma', 'options', 'news', 'misc', 'admin'] as const;
export type ToolCategory = (typeof TOOL_CATEGORIES)[number];

export const TOOL_CATEGORY_LABELS: Record<ToolCategory, string> = {
  gamma: 'Gamma',
  options: 'Options',
  news: 'News & Commentary',
  misc: 'Misc',
  admin: 'Admin',
};

export interface ToolPermissionConfig {
  toolType: ToolType;
  displayName: string;
  description: string;
  requiredPermission?: string | string[]; // If undefined, tool is free for all. If array, user needs ANY of them.
  adminOnly?: boolean; // If true, hide entirely from users without permission
  addedAt?: string; // ISO date - controls sort order in AddToolModal (newest first)
  temporaryBadge?: TemporaryBadgeConfig; // For "New" and "Updated" badges (expires 1 week after addedAt)

  /**
   * Drop from the Add Tool catalog. Read ONLY by `AddToolModal`.
   *
   * ⚠️ This hides the tool; it does NOT remove it from dashboards people have
   * already built. Those keep working, deliberately — `REMOVED_WIDGET_TYPES` in
   * `DashboardContext` is the destructive counterpart, and it is one-way: the
   * cleaned layout is written back, so a user who had the widget simply finds it
   * gone. Nothing here is listed there.
   */
  hiddenFromCatalog?: boolean;

  /**
   * Which section of the catalog this appears under.
   *
   * Optional, and anything without one falls into `misc` — a new tool should
   * land somewhere sensible by default rather than vanish from the catalog
   * because a field was missed. Ignored for `adminOnly` tools, which are always
   * grouped under `admin` at the bottom.
   */
  category?: ToolCategory;
}

// Define all permissions (these match backend stripe_metadata and manual_permissions)
export const PERMISSIONS: Record<string, PermissionConfig> = {
  'wiseguy-pro': {
    id: 'wiseguy-pro',
    displayName: 'Wiseguy Pro',
    description: 'Access to advanced unusual options activity alerts',
  },
  'premium': {
    id: 'premium',
    displayName: 'Premium',
    description: 'Full access to all tools',
  },
  'jdash_access': {
    id: 'jdash_access',
    displayName: 'JDash Admin',
    description: 'Administrative access to JDash tools',
  },
  'discord_composer': {
    id: 'discord_composer',
    displayName: 'Discord Composer',
    description: 'Access to Discord message composer',
  },
  steamroom: {
    id: 'steamroom',
    displayName: 'FZN Pro',
    description: 'Access to premium FZN trading tools',
  },
  zeroday: {
    id: 'zeroday',
    displayName: 'FZN Zeroday',
    description: 'Access to FZN Zeroday / Futures chat channel',
  },
  wsjonly: {
    id: 'wsjonly',
    displayName: 'WSJ Only',
    description: 'Access to WSJ Only features',
  },
  'Alpha': {
    id: 'Alpha',
    displayName: 'Alpha',
    description: 'Access to alpha features for testing and feedback',
  },
  'admin_only': {
    id: 'admin_only',
    displayName: 'Admin Only',
    description: 'Pre-alpha internal previews. Granted manually to specific users.',
  },
};

// Define tool permissions (what permission is required for each tool)
// addedAt controls sort order in AddToolModal (newest first)
export const TOOL_PERMISSIONS: ToolPermissionConfig[] = [
  {
    toolType: 'wiseguy-alerts',
    displayName: 'Wiseguy Alerts',
    description: 'Filtered real-time options flow',
    requiredPermission: ['steamroom', 'wsjonly'],
    addedAt: '2024-06-01',
    category: 'options',
  },
  {
    toolType: 'squeezo',
    displayName: 'Squeeze-O-Meter',
    description: 'Market squeeze indicator (0-100)',
    requiredPermission: 'jdash_access',
    adminOnly: true,
    addedAt: '2023-06-01',
  },
  {
    toolType: 'steamroom-levels',
    displayName: 'Fuzion Levels',
    description: 'Key market levels with GEX walls and pivot levels',
    requiredPermission: ['steamroom', 'zeroday'],
    addedAt: '2024-06-01',
    category: 'misc',
  },
  {
    toolType: 'upgrades-downgrades',
    displayName: 'Upgrades & Downgrades',
    description: "Today's analyst rating changes",
    requiredPermission: 'steamroom',
    addedAt: '2024-08-01',
    // Retired: Per-ticker version lives on Ticker Explorer (AnalystRatingsSection).
    hiddenFromCatalog: true,
  },
  {
    toolType: 'congress-trades',
    displayName: 'Congress Trades',
    description: 'Congressional stock trading disclosures',
    requiredPermission: 'steamroom',
    addedAt: '2024-08-01',
    // Retired: Lives on Ticker Explorer (CongressTradesSection).
    hiddenFromCatalog: true,
  },
  {
    toolType: 'insider-trades',
    displayName: 'Insider Trades',
    description: 'SEC Form 4 insider trading disclosures',
    requiredPermission: 'steamroom',
    addedAt: '2026-03-19',
    temporaryBadge: { type: 'new' },
    // Retired: Lives on Ticker Explorer (InsiderTradesSection).
    hiddenFromCatalog: true,
  },
  {
    toolType: 'market-movers',
    displayName: 'Market Movers',
    description: 'Biggest gainers, losers, and most active stocks',
    requiredPermission: 'steamroom',
    addedAt: '2026-03-19',
    temporaryBadge: { type: 'new' },
    category: 'misc',
  },
  {
    toolType: 'td-sequential',
    displayName: 'Niner Chart',
    description: 'Price chart with TD Sequential indicators',
    adminOnly: true,
    addedAt: '2024-07-01',
  },
  {
    toolType: 'niner-breadth',
    displayName: 'Niner Breadth',
    description: 'TD Sequential z-score market breadth indicator',
    adminOnly: true,
    addedAt: '2024-07-15',
  },
  {
    toolType: 'gex-profile',
    displayName: 'Gamma Exposure',
    description: 'End of day naive gamma exposure profile',
    requiredPermission: 'steamroom',
    addedAt: '2024-09-01',
    category: 'gamma',
  },
  {
    toolType: 'gex-ray',
    displayName: 'GEX-Ray',
    description: 'Real-time intraday naive gamma exposure',
    requiredPermission: ['steamroom', 'zeroday'],
    addedAt: '2024-10-01',
    category: 'gamma',
  },
  {
    toolType: 'gex-heatmap',
    displayName: 'Gamma Heatmap',
    description: 'EOD dealer remaining-book gamma on a price × date grid',
    requiredPermission: 'Alpha',
    adminOnly: true,
    addedAt: '2026-09-09',
    temporaryBadge: { type: 'new' },
  },
  {
    toolType: 'nq-scalper',
    displayName: 'Candle Chart',
    description: 'OpenAlgo candle chart with the Zeroday Scalper indicator',
    requiredPermission: ['steamroom', 'zeroday', 'wsjonly'],
    addedAt: '2026-09-29',
    temporaryBadge: { type: 'new' },
    category: 'misc',
  },
  {
    toolType: 'zeroday-breadth',
    displayName: 'Zeroday Breadth',
    description: 'MAG7, VIX, ADDQ and ADD panes for under the Candle Chart',
    requiredPermission: ['steamroom', 'zeroday', 'wsjonly'],
    addedAt: '2026-10-28',
    temporaryBadge: { type: 'new' },
    category: 'misc',
  },
  {
    toolType: 'cumulative-premium',
    displayName: 'Net Sweeper Flow',
    description: 'Real-time cumulative call / put premium from option sweeps',
    requiredPermission: 'steamroom',
    addedAt: '2025-12-15',
    temporaryBadge: { type: 'updated' },
    category: 'options',
  },
  {
    toolType: 'fj-economic-calendar',
    displayName: 'Economic Calendar',
    description: 'Real-time economic events and calendar (FinancialJuice)',
    addedAt: '2024-07-01',
    // Retired: The /calendar page carries economic releases now, with the
    // expirations and holidays alongside them.
    hiddenFromCatalog: true,
  },
  {
    toolType: 'fj-voice',
    displayName: 'FJ Voice Squawk',
    description: 'Live audio market commentary (FinancialJuice)',
    addedAt: '2023-07-01',
    // Retired: The AudioDrawer serves this through SquawkPlayer, which is
    // where people reach for audio.
    hiddenFromCatalog: true,
  },
  {
    toolType: 'fj-tickstrike',
    displayName: 'Tickstrike',
    description: 'Real-time market movement ticker (FinancialJuice)',
    requiredPermission: ['steamroom', 'zeroday'],
    addedAt: '2023-07-01',
    // Retired: The AudioDrawer serves this through SquawkPlayer.
    hiddenFromCatalog: true,
  },
  {
    toolType: 'fj-headlines',
    displayName: 'Headlines',
    description: 'Real-time market moving news (FinancialJuice)',
    requiredPermission: ['steamroom', 'zeroday'],
    addedAt: '2024-07-01',
    category: 'news',
  },
  {
    toolType: 'discord-composer',
    displayName: 'Discord Composer',
    description: 'Send messages to Discord webhooks',
    requiredPermission: ['jdash_access', 'discord_composer'],
    adminOnly: true,
    addedAt: '2023-06-15',
  },
  {
    toolType: 'sentiment-control',
    displayName: 'Sentiment Control',
    description: 'Set Squeezo and sentiment indicator values',
    requiredPermission: ['jdash_access', 'discord_composer'],
    adminOnly: true,
    addedAt: '2023-06-15',
  },
  {
    toolType: 'wsj-feed',
    displayName: 'WSJ Feed',
    description: 'Wall St. Jesus alerts and commentary feed',
    requiredPermission: ['steamroom', 'wsjonly'],
    addedAt: '2026-02-18',
    temporaryBadge: { type: 'new' },
    category: 'news',
  },
];

/**
 * Full-page views that carry a tier gate.
 *
 * Deliberately NOT part of `ToolType`: that union is the vocabulary of widget
 * instances persisted in saved dashboard layouts (`ToolWindow.type`). A view is
 * a route, never a dashboard tile, so adding these there would let a view id be
 * written into someone's stored layout.
 */
export const VIEW_TYPES = ['ticker-explorer', 'calendars', 'market-overview'] as const;
export type ViewType = (typeof VIEW_TYPES)[number];

export interface ViewPermissionConfig {
  viewType: ViewType;
  displayName: string;
  /** User needs ANY of these. Order matters: [0] is the tier shown on the upgrade prompt. */
  requiredPermission: string[];
}

/**
 * Route-level tier gates, mirroring the entitlement matrix.
 *
 * ⚠️ These are a UX gate, not a security boundary. Ticker Explorer is enforced
 * server-side too (`HasSteamroomAccess` on the FMP proxy views). Calendars and
 * Market Overview read QuantForge directly, so their server-side scoping lives
 * in the QF token claims rather than a portal permission class — a user who
 * calls QF directly is not stopped by anything here.
 */
export const VIEW_PERMISSIONS: ViewPermissionConfig[] = [
  {
    viewType: 'ticker-explorer',
    displayName: 'Ticker Explorer',
    requiredPermission: ['steamroom'],
  },
  {
    viewType: 'calendars',
    displayName: 'Calendars',
    requiredPermission: ['steamroom', 'zeroday'],
  },
  {
    viewType: 'market-overview',
    displayName: 'Market Overview',
    requiredPermission: ['steamroom', 'zeroday'],
  },
];

export function getViewPermissionConfig(
  viewType: ViewType
): ViewPermissionConfig | undefined {
  return VIEW_PERMISSIONS.find((config) => config.viewType === viewType);
}

// Upgrade URL
export const UPGRADE_URL = 'https://fzntrading.com/pricing';

// Helper functions
export function getToolPermissionConfig(toolType: ToolType): ToolPermissionConfig | undefined {
  return TOOL_PERMISSIONS.find((config) => config.toolType === toolType);
}

export function getPermissionConfig(permissionId: string): PermissionConfig | undefined {
  return PERMISSIONS[permissionId];
}

// Badge helper constants
const TEMPORARY_BADGE_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 1 week

// Returns the permanent badge (admin/beta) - auto-inferred, never expires
export function getPermanentBadge(toolType: ToolType): PermanentBadgeType | null {
  const config = getToolPermissionConfig(toolType);
  if (!config?.adminOnly) return null;

  const perms = Array.isArray(config.requiredPermission)
    ? config.requiredPermission
    : config.requiredPermission
      ? [config.requiredPermission]
      : [];

  if (perms.includes('Alpha')) return 'beta';
  return 'admin';
}

// Returns the temporary badge (new/updated) if not expired
export function getTemporaryBadge(toolType: ToolType): TemporaryBadgeConfig | null {
  const config = getToolPermissionConfig(toolType);
  if (!config?.temporaryBadge || !config.addedAt) return null;

  const addedDate = new Date(config.addedAt);
  const expiryDate = new Date(addedDate.getTime() + TEMPORARY_BADGE_DURATION_MS);

  if (new Date() > expiryDate) return null;
  return config.temporaryBadge;
}

// Combined helper for convenience - returns badge info or null
export function getToolBadge(toolType: ToolType): { type: BadgeType; isTemporary: boolean } | null {
  const temp = getTemporaryBadge(toolType);
  if (temp) return { type: temp.type, isTemporary: true };

  const perm = getPermanentBadge(toolType);
  if (perm) return { type: perm, isTemporary: false };

  return null;
}

// For sorting in AddToolModal - returns addedAt timestamp or 0
export function getToolSortDate(toolType: ToolType): number {
  const config = getToolPermissionConfig(toolType);
  if (!config?.addedAt) return 0; // No date = sort to bottom
  return new Date(config.addedAt).getTime();
}
